// react
import { ReactNode, useContext, useMemo, useState } from 'react';

// types
import { ActionButton, Entity, Pagination } from '../../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// context
import { ContextUserEntity } from '../../../../App.context';

// mui
import { Grid } from '@mui/material';

// ui
import { BtnExport } from '../../../ui/BtnExport/BtnExport';
import { BtnFilters } from '../../../ui/BtnFilters/BtnFilters';
import { EmptyProps } from '../../../ui/Empty/Empty';
import { TableHeaderProps } from '../../../ui/TableHeaders/TableHeader/TableHeader';
import { TableHeaders } from '../../../ui/TableHeaders/TableHeaders';
import { TableFooter } from '../../../ui/TableFooter/TableFooter';

// cards
import { CardEmpty } from '../../../cards/CardEmpty/CardEmpty';
import { CardBeneficiary } from '../../../cards/CardBeneficiary/CardBeneficiary';

// forms
import { FormSearch } from '../../../forms/FormSearch/FormSearch';

// icons
import { PeopleOutlineOutlined as IconPeople } from '@mui/icons-material';
import { FilterChangeEvent, FilterProps } from '../../../ui/Filters/Filters';

// config
import { featureFlags } from '../../../../config/featureFlags';

// pages
import { ContextUserProjectPermissions } from '../../../pages/Project/Project.context';

// props
export interface TableBeneficiariesProps {
	isLoading?: boolean;
	pagination: Pagination;
	beneficiaries: Entity[];
	refetchBeneficiaries?: () => void;
}

export const TableBeneficiaries = ({
	isLoading,
	pagination,
	beneficiaries,
	refetchBeneficiaries,
}: TableBeneficiariesProps) => {
	// hooks
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramHasTransactions = searchParams.get('hasTransactions');
	const paramQ = searchParams.get('q');
	const paramSubtype = searchParams.get('subtype');

	// defaults
	const defaultFiltersValues = {
		subtype: 'all',
		hasTransactions: 'all',
	};

	// state
	const [filtersValues, setFiltersValues] = useState({
		hasTransactions: paramHasTransactions || defaultFiltersValues.hasTransactions,
		subtype: paramSubtype || defaultFiltersValues.subtype,
	});

	// context
	const { userEntity } = useContext(ContextUserEntity);
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// vars
	const formId = 'form-beneficiaries-filters';

	// columns
	const nameColWidth = 2;
	const walletColWidth = 1.5;
	const entityColWidth = 1.5;
	const typeColWidth = 1;
	const statusColWidth = 1;
	const paidTotalBenefitsColWidth = 1;
	const updatedColWidth = 1;
	const traceabilityColWidth = 1;

	// headers
	const headers: TableHeaderProps[] = [
		{
			GridProps: {
				lg: nameColWidth,
			},
			isSortable: true,
			label: t('name'),
			value: 'name',
		},
		{
			GridProps: {
				lg: walletColWidth,
			},
			label: t('wallet'),
			value: 'wallet',
		},
		{
			GridProps: {
				lg: typeColWidth,
			},
			label: t('type'),
			value: 'type',
		},
		{
			GridProps: {
				lg: entityColWidth,
			},
			label: t('entity'),
			value: 'parent',
		},
		{
			GridProps: {
				lg: statusColWidth,
			},
			label: t('status'),
			value: 'status',
		},
		{
			GridProps: {
				lg: paidTotalBenefitsColWidth,
			},
			label: t('benefits'),
			value: 'benefits',
		},
		{
			GridProps: {
				lg: traceabilityColWidth,
			},
			label: t('transactionsEvidenced'),
			tooltip: {
				title: t('tooltip.transactionsEvidenced'),
			},
			value: 'traceability',
		},
	];

	// Add financial traceability column conditionally based on feature flag
	if (featureFlags['FINANCIAL_TRACEABILITY']) {
		headers.push({
			GridProps: {
				lg: 1,
			},
			label: t('financialValueEvidenced'),
			tooltip: {
				title: t('tooltip.financialValueEvidenced'),
			},
			value: 'traceability',
		});
	}

	headers.push({
		GridProps: {
			lg: updatedColWidth,
		},
		label: t('updated'),
		value: 'updated',
	});

	const handleChangeFilterValue = (e: FilterChangeEvent) => {
		const { name, value } = e.target as HTMLInputElement;
		setFiltersValues({
			...filtersValues,
			[name]: value,
		});
	};

	// filters
	const filters: FilterProps[] = [
		{
			label: t('type'),
			hasAllOption: true,
			hasNoneOption: false,
			hasSearchParam: true,
			id: `${formId}-subtype`,
			name: 'subtype',
			onChange: handleChangeFilterValue,
			options: [
				{
					label: t('organization'),
					value: 'ORGANIZATION',
				},
				{
					label: t('individual'),
					value: 'INDIVIDUAL',
				},
			],
			type: 'SELECT',
			value: filtersValues.subtype,
		},
		{
			label: t('paid'),
			hasAllOption: true,
			hasNoneOption: false,
			hasSearchParam: true,
			id: `${formId}-hasTransactions`,
			name: 'hasTransactions',
			onChange: handleChangeFilterValue,
			options: [
				{
					label: t('yes'),
					value: 'true',
				},
				{
					label: t('no'),
					value: 'false',
				},
			],
			type: 'SELECT',
			value: filtersValues.hasTransactions,
		},
	];

	// memo
	const empty = useMemo(() => {
		const emptyProps: EmptyProps = {
			actions: [],
			content: { children: t('empty.beneficiary.content') },
			IconCircleProps: { icon: (<IconPeople />) as ReactNode },
			title: { children: t('empty.beneficiary.title') },
		};

		if (userEntity && userProjectPermissions?.beneficiary?.create && emptyProps.actions) {
			emptyProps.actions.push(
				{
					children: t('addBeneficiary'),
					onClick: () => {
						searchParams.set('dialog', 'addBeneficiary');
						setSearchParams(searchParams);
					},
					variant: 'contained' as ActionButton['variant'],
				},
				{
					children: t('uploadBeneficiaries'),
					onClick: () => {
						searchParams.set('dialog', 'uploadBeneficiaries');
						setSearchParams(searchParams);
					},
					variant: 'outlined' as ActionButton['variant'],
				}
			);
		}

		if (paramQ) {
			emptyProps.actions = [];
			emptyProps.content = { children: t('empty.default.content') };
			emptyProps.IconCircleProps = { icon: null };
		}

		return emptyProps;
	}, [paramQ, searchParams, setSearchParams, t, userEntity, userProjectPermissions]);

	const content = useMemo(() => {
		if (isLoading || !beneficiaries.length) {
			return <CardEmpty empty={empty} isLoading={isLoading} />;
		}

		return (
			<Grid container spacing={2}>
				{beneficiaries.map((beneficiary, i) => {
					return (
						<Grid item key={`beneficiary-${i}`} xs={12}>
							<CardBeneficiary
								beneficiary={beneficiary}
								refetchBeneficiaries={refetchBeneficiaries}
							/>
						</Grid>
					);
				})}
			</Grid>
		);
	}, [empty, isLoading, beneficiaries, refetchBeneficiaries]);

	return (
		<Grid container spacing={2}>
			<Grid item xs={12}>
				<Grid container spacing={2}>
					<Grid item xs={12} md={true}>
						<FormSearch InputProps={{ placeholder: t('searchByIdOrName') }} />
					</Grid>
					<Grid item xs={6} md="auto">
						<BtnExport resource={'BENEFICIARY'} />
					</Grid>
					<Grid item xs={6} md="auto">
						<BtnFilters
							filters={filters}
							onReset={() => {
								setFiltersValues(defaultFiltersValues);
							}}
						/>
					</Grid>
				</Grid>
			</Grid>

			<Grid item xs={12} sx={{ display: { xs: 'none', lg: 'block' } }}>
				<TableHeaders headers={headers} />
			</Grid>
			<Grid item xs={12}>
				{content}
			</Grid>
			<Grid item xs={12}>
				<TableFooter numShowing={beneficiaries.length} pagination={pagination} />
			</Grid>
		</Grid>
	);
};
